<template>
  <el-container class="admin-dashboard">
    <!-- 左侧导航栏 -->
    <el-aside :width="isCollapse ? '64px' : '220px'" class="sidebar">
      <div class="sidebar-logo">
        <span v-if="!isCollapse">工单管理系统</span>
        <span v-else>BX</span>
      </div>
      <el-menu
        :default-active="activePanel"
        :collapse="isCollapse"
        class="sidebar-menu"
        @select="handleMenuSelect"
      >
        <el-menu-item-group v-if="userStore.isAdmin || userStore.isAfterSales" title="保修管理">
          <el-menu-item index="repairList">
            <el-icon><Tickets /></el-icon>
            <template #title>
              <el-badge
                :value="repairPendingCount"
                :hidden="repairPendingCount <= 0"
                :max="99"
                class="menu-badge"
              >
                保修台账
              </el-badge>
            </template>
          </el-menu-item>
        </el-menu-item-group>

        <el-menu-item-group title="工单管理">
          <el-menu-item index="workOrderList">
            <el-icon><Tickets /></el-icon>
            <template #title>
              <el-badge
                :value="workOrderOvertimeCount"
                :hidden="workOrderOvertimeCount <= 0"
                :max="99"
                class="menu-badge menu-badge-danger"
              >
                工单台账
              </el-badge>
            </template>
          </el-menu-item>
          <el-menu-item index="workOrderProcess">
            <el-icon><Tools /></el-icon>
            <template #title>处理工单</template>
          </el-menu-item>
          <el-menu-item index="workOrderLogs">
            <el-icon><Collection /></el-icon>
            <template #title>处理日志</template>
          </el-menu-item>
          <el-menu-item index="workOrderCreate">
            <el-icon><DocumentAdd /></el-icon>
            <template #title>创建工单</template>
          </el-menu-item>
          <el-menu-item index="categoryManagement">
            <el-icon><Collection /></el-icon>
            <template #title>工单分类</template>
          </el-menu-item>
        </el-menu-item-group>

        <el-menu-item-group v-if="userStore.isAdmin || userStore.isAfterSales" title="主数据">
          <el-menu-item index="brandProducts">
            <el-icon><Goods /></el-icon>
            <template #title>产品目录</template>
          </el-menu-item>
          <el-menu-item index="cooperativeChains">
            <el-icon><OfficeBuilding /></el-icon>
            <template #title>合作连锁店</template>
          </el-menu-item>
          <el-menu-item v-if="FEATURE_DEVICE_ARCHIVES" index="deviceArchives">
            <el-icon><Files /></el-icon>
            <template #title>设备资料</template>
          </el-menu-item>
          <el-menu-item index="partMasters">
            <el-icon><Box /></el-icon>
            <template #title>配件资料</template>
          </el-menu-item>
        </el-menu-item-group>

        <el-menu-item-group v-if="userStore.isAdmin || userStore.isAfterSales" title="数据与系统">
          <el-menu-item index="issueStats">
            <el-icon><DataAnalysis /></el-icon>
            <template #title>统计分析</template>
          </el-menu-item>
        </el-menu-item-group>

        <el-menu-item-group v-if="userStore.isAdmin" title="系统管理">
          <el-menu-item index="userManagement">
            <el-icon><User /></el-icon>
            <template #title>用户管理</template>
          </el-menu-item>
        </el-menu-item-group>
      </el-menu>
    </el-aside>

    <!-- 右侧主区域 -->
    <el-container class="right-container">
      <!-- 顶部导航栏 -->
      <el-header class="header">
        <div class="header-left">
          <el-icon class="collapse-btn" @click="isCollapse = !isCollapse">
            <Fold v-if="!isCollapse" />
            <Expand v-else />
          </el-icon>
          <h2>{{ activePanelLabel }}</h2>
        </div>
        <div class="header-right">
          <el-dropdown @command="handleCommand" trigger="click">
            <div class="user-info">
              <el-avatar :size="32" :src="userStore.userInfo.avatar">
                {{ userStore.userInfo.username?.charAt(0)?.toUpperCase() }}
              </el-avatar>
              <span class="username">{{ userStore.userInfo.username }}</span>
              <el-icon><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>

      <!-- 主要内容区域 -->
      <el-main class="main-content">
        <!-- 欢迎信息卡片（渐变 Hero） -->
        <div class="welcome-hero">
          <div class="welcome-text">
            <h1>👋 欢迎回来，{{ userStore.userInfo.username }}！</h1>
            <p>今天是 {{ currentDate }}，祝您工作愉快！</p>
          </div>
          <div class="welcome-decoration">
            <el-icon><DataAnalysis /></el-icon>
          </div>
        </div>

        <el-alert
          v-if="visibleReminderItems.length"
          class="timeout-reminder-bar"
          :title="`当前有 ${visibleReminderItems.length} 条售后时效提醒`"
          type="warning"
          show-icon
          :closable="true"
          @close="handleReminderClose"
        >
          <template #default>
            <div class="timeout-reminder-list">
              <div v-for="item in visibleReminderItems" :key="item.key" class="timeout-reminder-item">
                <el-tag size="small" :type="item.tagType">{{ item.tagText }}</el-tag>
                <span>{{ item.message }}</span>
              </div>
            </div>
          </template>
        </el-alert>

        <!-- 管理功能面板（动态组件切换） -->
        <el-card class="panel-card" shadow="never">
          <template #header>
            <div class="card-header">
              <span class="card-title">
                <span class="title-bar"></span>
                {{ activePanelLabel }}
              </span>
            </div>
          </template>
          <div class="panel-container">
            <component
              :is="activePanelComponent"
              :key="activePanel"
              v-bind="activePanelProps"
              @navigate="handleDashboardNavigate"
              @created="handleWorkOrderCreated"
            />
          </div>
        </el-card>
      </el-main>

      <el-footer class="footer">
        <div class="footer-content">
          <span>© 2026 北磁科技（内部系统，请勿外传）</span>
        </div>
      </el-footer>
    </el-container>
  </el-container>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  ArrowDown,
  Tools,
  DataAnalysis,
  User,
  Tickets,
  DocumentAdd,
  Collection,
  Files,
  Box,
  Goods,
  OfficeBuilding,
  Fold,
  Expand
} from '@element-plus/icons-vue'
import WorkOrderList from '@/components/WorkOrderList.vue'
import RepairList from '@/components/RepairList.vue'
import WorkOrderCreateForm from '@/components/WorkOrderCreateForm.vue'
import UserManagement from '@/components/UserManagement.vue'
import WorkOrderCategoryManagement from '@/components/WorkOrderCategoryManagement.vue'
import StatisticsCenter from '@/components/StatisticsCenter.vue'
import MasterDataManagement from '@/components/MasterDataManagement.vue'
import { FEATURE_DEVICE_ARCHIVES } from '@/config/featureFlags'
import { RepairAPI } from '@/request/repair'
import { getWorkOrderSlaMeta } from '@/utils/workOrderUi'
import WorkOrderProcessPanel from '@/components/WorkOrderProcessPanel.vue'
import WorkOrderLogPanel from '@/components/WorkOrderLogPanel.vue'
import { TicketsAPI } from '@/request/tickets'

export default {
  name: 'HomeView',
  components: {
    ArrowDown,
    Tools,
    DataAnalysis,
    User,
    Tickets,
    DocumentAdd,
    Collection,
    Files,
    Box,
    OfficeBuilding,
    Fold,
    Expand,
    WorkOrderList,
    RepairList,
    WorkOrderCreateForm,
    UserManagement,
    WorkOrderCategoryManagement,
    StatisticsCenter,
    MasterDataManagement,
    WorkOrderProcessPanel,
    WorkOrderLogPanel
  },
  setup() {
    const router = useRouter()
    const userStore = useUserStore()
    const reminderTimer = ref(null)
    const reminderItems = ref([])
    const reminderDismissed = ref(false)
    const reminderSignature = ref('')
    
    // 响应式数据
    
    
    // 早期台账相关的模拟状态已移除
    
    // 侧边栏折叠状态
    const isCollapse = ref(false)

    // 面板切换相关
    const activePanel = ref(userStore.isAfterSales ? 'workOrderProcess' : 'workOrderList')
    const repairListContext = ref({})
    const panelLabelMap = {
      repairList: '保修台账',
      workOrderList: '工单台账',
      workOrderProcess: '处理工单',
      workOrderLogs: '处理日志',
      workOrderCreate: '创建工单',
      cooperativeChains: '合作连锁店',
      deviceArchives: '设备资料',
      partMasters: '配件资料',
      brandProducts: '产品目录',
      issueStats: '统计分析',
      categoryManagement: '工单分类',
      userManagement: '用户管理'
    }
    const activePanelLabel = computed(() => panelLabelMap[activePanel.value])
    const activePanelComponent = computed(() => {
      switch (activePanel.value) {
        case 'workOrderCreate':
          return WorkOrderCreateForm
        case 'workOrderProcess':
          return WorkOrderProcessPanel
        case 'workOrderLogs':
          return WorkOrderLogPanel
        case 'brandProducts':
        case 'cooperativeChains':
        case 'deviceArchives':
        case 'partMasters':
          return MasterDataManagement
        case 'issueStats':
          return StatisticsCenter
        case 'categoryManagement':
          return WorkOrderCategoryManagement
        case 'userManagement':
          return UserManagement
        case 'repairList':
          return RepairList
        case 'workOrderList':
        default:
          return WorkOrderList
      }
    })
    const activePanelProps = computed(() => {
      if (activePanel.value === 'repairList') {
        return {
          externalFilters: repairListContext.value,
          hideSourceFilter: true,
          apiMode: 'repairs'
        }
      }
      if (activePanel.value === 'workOrderList') {
        return {
          hideSourceFilter: false
        }
      }
      if (activePanel.value === 'workOrderProcess') {
        return {}
      }
      if (activePanel.value === 'workOrderLogs') {
        return {}
      }
      if (activePanel.value === 'workOrderCreate') {
        return {
          submitLabel: '创建工单',
          successMessage: '工单创建成功'
        }
      }
      if (activePanel.value === 'categoryManagement') {
        return {
          hideScopeFilter: false
        }
      }
      if (activePanel.value === 'brandProducts') {
        return {
          initialTab: 'products'
        }
      }
      if (activePanel.value === 'cooperativeChains') {
        return {
          initialTab: 'chains'
        }
      }
      if (activePanel.value === 'deviceArchives') {
        return {
          initialTab: 'devices'
        }
      }
      if (activePanel.value === 'partMasters') {
        return {
          initialTab: 'parts'
        }
      }
      return {}
    })
    
    // 计算属性
    const currentDate = computed(() => {
      return new Date().toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      })
    })

    const formatDuration = (ms) => {
      const totalMinutes = Math.max(0, Math.floor(ms / 60000))
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      if (hours <= 0) return `${minutes} 分钟`
      return `${hours} 小时 ${minutes} 分钟`
    }

    const visibleReminderItems = computed(() => (
      reminderDismissed.value ? [] : reminderItems.value
    ))

    const buildReminderItems = (tickets) => {
      const now = Date.now()
      return (Array.isArray(tickets) ? tickets : []).flatMap((ticket) => {
        const status = ticket?.status
        const reportAt = ticket?.reportTime || ticket?.createdAt
        const updatedAt = ticket?.updatedAt || ticket?.reportTime || ticket?.createdAt
        const code = ticket?.workOrderNo || `工单#${ticket?.id}`
        const shipmentText = [
          ticket?.handlingMethod,
          ticket?.processResult,
          ticket?.serviceContent,
          ticket?.summary
        ].filter(Boolean).join(' ')
        const isShipmentTicket = /邮寄|补发|快递|物流|寄送|发配件|寄配件/i.test(shipmentText)
        const reminderHours = isShipmentTicket ? 24 : 6
        const dangerHours = reminderHours * 2
        const reminderMs = reminderHours * 3600000
        const dangerMs = dangerHours * 3600000
        const items = []
        if (status === 'PENDING' && reportAt) {
          const elapsed = now - new Date(reportAt).getTime()
          if (elapsed >= dangerMs) {
            items.push({
              key: `pending${dangerHours}-${ticket.id}`,
              tagType: 'danger',
              tagText: `${dangerHours}小时未处理`,
              message: `${code} 已过去 ${formatDuration(elapsed)}，已超过 ${dangerHours} 小时未处理`
            })
          } else if (elapsed >= reminderMs) {
            items.push({
              key: `pending${reminderHours}-${ticket.id}`,
              tagType: 'warning',
              tagText: `${reminderHours}小时未处理`,
              message: `${code} 已过去 ${formatDuration(elapsed)}，已超过 ${reminderHours} 小时未处理`
            })
          }
        }
        if (status === 'PROCESSING' && updatedAt) {
          const elapsed = now - new Date(updatedAt).getTime()
          if (elapsed >= dangerMs) {
            items.push({
              key: `processing${dangerHours}-${ticket.id}`,
              tagType: 'danger',
              tagText: `${dangerHours}小时未变更`,
              message: `${code} 已过去 ${formatDuration(elapsed)}，已超过 ${dangerHours} 小时未变更`
            })
          } else if (elapsed >= reminderMs) {
            items.push({
              key: `processing${reminderHours}-${ticket.id}`,
              tagType: 'warning',
              tagText: `${reminderHours}小时未变更`,
              message: `${code} 已过去 ${formatDuration(elapsed)}，已超过 ${reminderHours} 小时未变更`
            })
          }
        }
        return items
      })
    }

    const checkTimeoutReminders = async () => {
      if (!(userStore.isAfterSales || userStore.isAdmin)) {
        return
      }
      try {
        const tickets = await TicketsAPI.getAll({ sourceType: 'AFTER_SALES' })
        const nextItems = buildReminderItems(tickets)
        const nextSignature = nextItems.map((item) => item.key).join('|')
        reminderItems.value = nextItems
        if (nextSignature !== reminderSignature.value) {
          reminderDismissed.value = false
          reminderSignature.value = nextSignature
        }
      } catch (error) {
        console.error('售后超时提醒检查失败:', error)
      }
    }

    const startReminderPolling = () => {
      if (!(userStore.isAfterSales || userStore.isAdmin)) {
        return
      }
      reminderDismissed.value = false
      checkTimeoutReminders()
      reminderTimer.value = window.setInterval(() => {
        checkTimeoutReminders()
      }, 60 * 1000)
    }

    const handleReminderClose = () => {
      reminderDismissed.value = true
    }

    // 处理下拉菜单命令
    const handleCommand = (command) => {
      switch (command) {
        case 'logout':
          logout()
          break
      }
    }

    // 退出登录
    const logout = async () => {
      try {
        await ElMessageBox.confirm('确定要退出登录吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        userStore.logout()
        router.push('/')
        ElMessage.success('已退出登录')
      } catch {
        // 用户取消操作
      }
    }

    // 菜单选择统一处理
    const handleMenuSelect = (index) => {
      if (index === 'repairList') {
        repairListContext.value = {}
        fetchRepairPending()
      }
      if (index === 'workOrderList') {
        fetchWorkOrderOvertime()
      }
      activePanel.value = index
    }

    // 菜单红点提醒：保修台账（待处理保修单）+ 工单台账（超时效未结单），全部后台角色可见
    const repairPendingCount = ref(0)
    const workOrderOvertimeCount = ref(0)
    let repairPollTimer = null
    const fetchRepairPending = async () => {
      try {
        const stats = await RepairAPI.getStatistics()
        repairPendingCount.value = Number(stats?.pending || 0)
      } catch (err) {
        // 静默失败，下轮轮询重试
      }
    }
    const fetchWorkOrderOvertime = async () => {
      try {
        const list = await TicketsAPI.getAll({ sourceType: 'AFTER_SALES' })
        const rows = Array.isArray(list) ? list : []
        workOrderOvertimeCount.value = rows.filter((t) => {
          if (t.status === 'CLOSED' || t.status === 'CANCELLED') return false
          return getWorkOrderSlaMeta(t).type === 'danger'
        }).length
      } catch (err) {
        // 静默失败，下轮轮询重试
      }
    }
    const startRepairPolling = () => {
      fetchRepairPending()
      fetchWorkOrderOvertime()
      repairPollTimer = window.setInterval(() => {
        fetchRepairPending()
        fetchWorkOrderOvertime()
      }, 60000)
    }

    // 导航方法
    const goToRepairList = () => {
      repairListContext.value = {}
      activePanel.value = 'repairList'
    }

    const goToWorkOrderList = () => {
      activePanel.value = 'workOrderList'
    }

    const goToWorkOrderProcess = () => {
      activePanel.value = 'workOrderProcess'
    }

    const goToWorkOrderLogs = () => {
      activePanel.value = 'workOrderLogs'
    }

    const goToStatistics = () => {
      activePanel.value = 'issueStats'
    }

    const goToWorkOrderCreate = () => {
      activePanel.value = 'workOrderCreate'
    }

    const goToCategoryManagement = () => {
      activePanel.value = 'categoryManagement'
    }

    const goToUserManagement = () => {
      activePanel.value = 'userManagement'
    }

    const handleDashboardNavigate = () => {}

    const handleWorkOrderCreated = () => {
      activePanel.value = 'workOrderList'
    }

    // 详情弹窗相关逻辑已移除

    // 组件挂载时获取数据
    // 已无挂载时的数据拉取（功能面板组件自行处理数据）

    const handleOpenRepairList = (event) => {
      repairListContext.value = {
        ...(event?.detail || {}),
        requestId: Date.now()
      }
      activePanel.value = 'repairList'
    }

    onMounted(() => {
      window.addEventListener('beici:open-repair-list', handleOpenRepairList)
      startReminderPolling()
      startRepairPolling()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('beici:open-repair-list', handleOpenRepairList)
      if (reminderTimer.value) {
        window.clearInterval(reminderTimer.value)
        reminderTimer.value = null
      }
      if (repairPollTimer) {
        window.clearInterval(repairPollTimer)
        repairPollTimer = null
      }
    })

    return {
      userStore,
      FEATURE_DEVICE_ARCHIVES,
      repairPendingCount,
      workOrderOvertimeCount,
      currentDate,
      handleCommand,
      logout,
      goToRepairList,
      goToWorkOrderList,
      goToWorkOrderProcess,
      goToWorkOrderLogs,
      goToWorkOrderCreate,
      goToStatistics,
      goToCategoryManagement,
      goToUserManagement,
      handleDashboardNavigate,
      handleWorkOrderCreated,
      handleMenuSelect,
      handleReminderClose,
      isCollapse,
      visibleReminderItems,

      activePanel,
      activePanelLabel,
      activePanelComponent,
      activePanelProps
    }
  }
}
</script>

<style scoped>
.admin-dashboard {
  height: 100vh;
  background-color: #f0f2f5;
}

/* ===== 左侧导航栏 ===== */
.sidebar {
  background: linear-gradient(180deg, #1f2a44 0%, #111827 100%);
  transition: width 0.28s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.12);
  z-index: 10;
}

.sidebar-logo {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 1px;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.sidebar-menu {
  border-right: none;
  flex: 1;
  background-color: transparent;
  padding: 8px 10px;
}

:deep(.sidebar-menu:not(.el-menu--collapse)) {
  width: 220px;
}

/* 深色菜单配色 */
:deep(.sidebar-menu .el-menu-item),
:deep(.sidebar-menu .el-sub-menu__title) {
  color: rgba(255, 255, 255, 0.72);
  height: 46px;
  line-height: 46px;
  margin: 4px 0;
  border-radius: 10px;
  transition: all 0.25s ease;
}

:deep(.sidebar-menu .el-menu-item .el-icon) {
  font-size: 18px;
}

:deep(.sidebar-menu .el-menu-item.is-active) {
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
}

:deep(.sidebar-menu .el-menu-item:not(.is-active):hover),
:deep(.sidebar-menu .el-sub-menu__title:hover) {
  background-color: rgba(255, 255, 255, 0.08);
  color: #fff;
}

:deep(.sidebar-menu .el-menu-item-group__title) {
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  letter-spacing: 1px;
  padding-left: 16px;
  margin-top: 6px;
}

/* ===== 右侧容器 ===== */
.right-container {
  height: 100vh;
  overflow: hidden;
}

.header {
  background: #fff;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  height: 64px;
  z-index: 5;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.collapse-btn {
  font-size: 20px;
  cursor: pointer;
  color: #606266;
  transition: all 0.2s;
  padding: 6px;
  border-radius: 8px;
}

.collapse-btn:hover {
  color: #2563eb;
  background-color: #f0f5ff;
}

.header-left h2 {
  margin: 0;
  color: #1f2937;
  font-size: 19px;
  font-weight: 600;
}

.header-right .user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 20px;
  transition: background-color 0.3s;
}

.header-right .user-info:hover {
  background-color: #f0f2f5;
}

:deep(.header-right .el-avatar) {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff;
  font-weight: 600;
}

.username {
  margin: 0 8px;
  color: #4b5563;
  font-weight: 500;
}

.main-content {
  padding: 24px;
  overflow-y: auto;
}

/* ===== 欢迎 Hero ===== */
.welcome-hero {
  position: relative;
  margin-bottom: 24px;
  padding: 28px 32px;
  border-radius: 16px;
  background: linear-gradient(120deg, #2563eb 0%, #4f46e5 50%, #7c3aed 100%);
  color: #fff;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(79, 70, 229, 0.25);
}

.welcome-hero::after {
  content: '';
  position: absolute;
  right: -60px;
  top: -60px;
  width: 220px;
  height: 220px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
}

.welcome-hero::before {
  content: '';
  position: absolute;
  right: 60px;
  bottom: -90px;
  width: 160px;
  height: 160px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 50%;
}

.welcome-text {
  position: relative;
  z-index: 1;
}

.welcome-text h1 {
  margin: 0 0 10px 0;
  font-size: 1.9rem;
  font-weight: 700;
}

.welcome-text p {
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
}

.welcome-decoration {
  position: absolute;
  right: 36px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 80px;
  color: rgba(255, 255, 255, 0.18);
  z-index: 1;
}

/* ===== 功能面板卡片 ===== */
.panel-card {
  border-radius: 14px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

:deep(.panel-card .el-card__header) {
  border-bottom: 1px solid #f0f2f5;
  padding: 16px 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.title-bar {
  display: inline-block;
  width: 4px;
  height: 18px;
  border-radius: 2px;
  background: linear-gradient(180deg, #3b82f6, #2563eb);
}

/* ===== 响应式设计 ===== */
@media (max-width: 768px) {
  .main-content {
    padding: 14px;
  }

  .welcome-hero {
    padding: 22px;
  }

  .welcome-decoration {
    display: none;
  }
}

@media (max-width: 480px) {
  .welcome-text h1 {
    font-size: 1.5rem;
  }
}
</style>
